'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';

/* ─── Data ───────────────────────────────────────────────────────────────── */
const ISSUE_TYPES = [
    { icon: 'pi-times-circle',    label: 'Cancel Order',   color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.25)' },
    { icon: 'pi-replay',          label: 'Return Request', color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.25)'  },
    { icon: 'pi-truck',           label: 'Delivery Issue', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.25)'  },
    { icon: 'pi-refresh',         label: 'Refund Status',  color: '#34d399', bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.25)'  },
    { icon: 'pi-wrench',          label: 'Damaged Product',color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.25)' },
    { icon: 'pi-question-circle', label: 'Other Queries',  color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',  border: 'rgba(148,163,184,0.2)'  },
];

const STEPS = [
    { num: '1', color: '#1bb8a7', icon: 'pi-shopping-bag', title: 'Go to My Orders',         desc: 'Visit the Orders section from your account.'              },
    { num: '2', color: '#6366f1', icon: 'pi-headphones',   title: 'Click "Help" on an order', desc: 'Each order card has a Help / Support button.'            },
    { num: '3', color: '#f59e0b', icon: 'pi-list',         title: 'Choose your issue',        desc: 'Select from the list — our team gets notified instantly.' },
];

/* ─── Auth helper ────────────────────────────────────────────────────────── */
const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

/* ─── Exported component ─────────────────────────────────────────────────── */
export const OrderTicketInfoPanel = ({ wrapStyle = {}, light = false }) => {
    const router = useRouter();
    const [open, setOpen] = useState(false);

    const handleGoToOrders = () => {
        const user = getAuthUser();
        router.push(user ? '/account/orders' : '/login?redirect=/account/orders');
    };

    /* ── colour tokens based on variant ── */
    const t = light ? {
        triggerBg:       open ? 'rgba(11,49,99,0.06)' : '#fff',
        triggerBorder:   open ? 'rgba(11,49,99,0.3)'  : 'rgba(11,49,99,0.15)',
        iconCircleBg:    open ? 'rgba(11,49,99,0.12)' : 'rgba(11,49,99,0.06)',
        iconCircleBdr:   open ? 'rgba(11,49,99,0.35)' : 'rgba(11,49,99,0.18)',
        iconColor:       open ? 'var(--primary-color)' : '#94a3b8',
        labelColor:      open ? 'var(--primary-color)' : '#475569',
        subColor:        '#94a3b8',
        chevronColor:    open ? 'var(--primary-color)' : '#94a3b8',
        bodyBg:          '#fff',
        bodyBorder:      'rgba(11,49,99,0.15)',
        bodyText:        '#475569',
        bodyStrong:      'var(--primary-color)',
        stepTitle:       'var(--primary-color)',
        stepDesc:        '#64748b',
        divider:         'rgba(11,49,99,0.1)',
        chipLabel:       '#64748b',
        ctaBg:           'var(--primary-color)',
        ctaShadow:       'rgba(11,49,99,0.3)',
    } : {
        triggerBg:       open ? 'rgba(99,102,241,0.14)' : 'rgba(255,255,255,0.05)',
        triggerBorder:   open ? 'rgba(99,102,241,0.4)'  : 'rgba(255,255,255,0.1)',
        iconCircleBg:    open ? 'rgba(99,102,241,0.3)'  : 'rgba(255,255,255,0.08)',
        iconCircleBdr:   open ? 'rgba(99,102,241,0.5)'  : 'rgba(255,255,255,0.12)',
        iconColor:       open ? '#a5b4fc' : 'rgba(255,255,255,0.5)',
        labelColor:      open ? '#c7d2fe' : 'rgba(255,255,255,0.65)',
        subColor:        'rgba(255,255,255,0.35)',
        chevronColor:    open ? '#a5b4fc' : 'rgba(255,255,255,0.3)',
        bodyBg:          'rgba(15,23,42,0.8)',
        bodyBorder:      'rgba(99,102,241,0.3)',
        bodyText:        'rgba(255,255,255,0.55)',
        bodyStrong:      'rgba(255,255,255,0.82)',
        stepTitle:       'rgba(255,255,255,0.88)',
        stepDesc:        '#94a3b8',
        divider:         '#f8faff',
        chipLabel:       '#94a3b8',
        ctaBg:           'linear-gradient(135deg, #1bb8a7, #6366f1)',
        ctaShadow:       'rgba(27,184,167,0.4)',
    };

    return (
        <div style={{ maxWidth: '44rem', margin: '0 auto 1.5rem', ...wrapStyle }}>

            {/* ── Trigger row ── */}
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    width:        '100%',
                    background:   t.triggerBg,
                    border:       `1px solid ${t.triggerBorder}`,
                    borderRadius: open ? '0.85rem 0.85rem 0 0' : '0.85rem',
                    padding:      '0.7rem 1rem',
                    cursor:       'pointer',
                    display:      'flex',
                    alignItems:   'center',
                    gap:          '0.65rem',
                    transition:   'all 0.22s',
                    textAlign:    'left',
                }}
            >
                <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    background: t.iconCircleBg,
                    border: `1px solid ${t.iconCircleBdr}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s',
                }}>
                    <i className="pi pi-info-circle" style={{ fontSize: '0.72rem', color: t.iconColor }} />
                </div>

                <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: t.labelColor, letterSpacing: '0.01em' }}>
                        Raise a ticket from your Order
                    </span>
                    <span style={{ fontSize: '0.7rem', color: t.subColor, marginLeft: 8 }}>
                        — cancel, return, delivery issue &amp; more
                    </span>
                </div>

                <i
                    className="pi pi-chevron-down"
                    style={{
                        fontSize:   '0.62rem',
                        color:       t.chevronColor,
                        transform:   open ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.25s ease',
                        flexShrink:  0,
                    }}
                />
            </button>

            {/* ── Expandable body ── */}
            <div style={{
                maxHeight:  open ? '600px' : '0',
                overflow:   'hidden',
                transition: 'max-height 0.38s cubic-bezier(.4,0,.2,1)',
            }}>
                <div style={{
                    background:     t.bodyBg,
                    border:         `1px solid ${t.bodyBorder}`,
                    borderTop:      'none',
                    borderRadius:   '0 0 0.85rem 0.85rem',
                    padding:        '1.1rem 1.2rem 1.25rem',
                    backdropFilter: light ? 'none' : 'blur(8px)',
                }}>
                    <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: t.bodyText, lineHeight: 1.6 }}>
                        <i className="pi pi-lightbulb mr-2" style={{ color: '#fbbf24', fontSize: '0.78rem' }} />
                        You can raise order-specific support tickets directly from your{' '}
                        <strong style={{ color: t.bodyStrong }}>My Orders</strong> page — without contacting us manually.
                    </p>

                    {/* Steps */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', marginBottom: '1rem', position: 'relative' }}>
                        <div style={{ position: 'absolute', left: 13, top: 14, bottom: 14, width: 1, background: t.divider }} />
                        {STEPS.map((step, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', position: 'relative' }}>
                                <div style={{
                                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                                    background: `${step.color}22`, border: `1.5px solid ${step.color}55`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                                }}>
                                    <span style={{ fontSize: '0.68rem', fontWeight: 800, color: step.color }}>{step.num}</span>
                                </div>
                                <div style={{ paddingTop: 3 }}>
                                    <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: t.stepTitle }}>
                                        <i className={`pi ${step.icon} mr-2`} style={{ fontSize: '0.7rem', color: step.color }} />
                                        {step.title}
                                    </p>
                                    <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: t.stepDesc, lineHeight: 1.5 }}>
                                        {step.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Divider */}
                    <div style={{ height: 1, background: t.divider, margin: '0.85rem 0' }} />

                    {/* Issue chips */}
                    <p style={{ margin: '0 0 0.6rem', fontSize: '0.68rem', color: t.chipLabel, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                        Supported issue types
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginBottom: '1.1rem' }}>
                        {ISSUE_TYPES.map((issue, i) => (
                            <span key={i} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '4px 10px', borderRadius: 999,
                                background: issue.bg, border: `1px solid ${issue.border}`,
                                fontSize: '0.72rem', fontWeight: 600, color: issue.color,
                            }}>
                                <i className={`pi ${issue.icon}`} style={{ fontSize: '0.62rem' }} />
                                {issue.label}
                            </span>
                        ))}
                    </div>

                    {/* CTA */}
                    <button
                        onClick={handleGoToOrders}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 7,
                            padding: '0.5rem 1.1rem', borderRadius: '0.6rem',
                            background: t.ctaBg,
                            border: 'none', color: '#fff',
                            fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                            boxShadow: `0 3px 14px ${t.ctaShadow}`,
                            transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                        <i className="pi pi-shopping-bag" style={{ fontSize: '0.75rem' }} />
                        Go to My Orders
                        <i className="pi pi-arrow-right" style={{ fontSize: '0.62rem', opacity: 0.8 }} />
                    </button>
                </div>
            </div>
        </div>
    );
};
